import clsx from 'clsx';
import LocaleCode from 'locale-code';
import uniq from 'lodash/uniq';
import { AvailableIconTypes } from '@ui/Icon';
import type { Actor } from '@avatar/types';
import {
  ActorVariant,
  ActorVariantType,
  ActorVariantVersions,
} from '../../../../server/graphql/__generated.types';
import { LocaleEnum_V2 } from '../../../../server/graphql/__generated-graphcms.types';
import { getDuplicateVoiceStyleVariantIDs } from './utils';

/**
 * Given and actor and variant, this function returns the presentational text for the variant.
 */
export const variantDisplayText = (variant: ActorVariant) => {
  const locale = LocaleCode.getCountryCode(variant.locale);

  return clsx(variant.variantType.label, {
    [`(${locale})`]: locale,
  });
};

/**
 * The variant type label, disambiguated with the locale when an avatar has
 * more than one variant of that style. `duplicateIDs` lets a caller iterating
 * an avatar's variants pass the set in once instead of it being recomputed
 * per variant.
 */
export const getVariantLabel = (
  avatar: Actor,
  variant: ActorVariant,
  duplicateIDs: Set<number> = getDuplicateVoiceStyleVariantIDs(avatar)
): string => {
  if (duplicateIDs.has(variant.id) && variant.locale) {
    return variantDisplayText(variant);
  }

  return variant.variantType.label;
};

export const isPartnerVoice = (actor: Actor) => {
  return !!actor.isPartnerVoice;
};

/**
 * @note consider Jilu Mandarin and Southwestern Mandarin if needed
 * @see https://learn.microsoft.com/en-us/azure/ai-services/speech-service/language-support?tabs=stt
 */
const LANGUAGE_MAP = {
  yue: 'Cantonese',
  wuu: 'Wu',
  zh: 'Mandarin',
};

const LANGUAGE_OVERRIDES = {
  [LocaleEnum_V2.YueCn]: 'Cantonese',
};

const COUNTRY_OVERRIDES = {
  [LocaleEnum_V2.EnGb]: 'Great Britain',
  [LocaleEnum_V2.EnHi]: 'India',
  [LocaleEnum_V2.EnUk]: 'United Kingdom',
  [LocaleEnum_V2.YueCn]: 'China',
};

export const getLanguageAndCountryFromLocaleCode = (locale: LocaleEnum_V2) => {
  const reformattedLocale = locale.replace('_', '-');
  const language =
    LANGUAGE_OVERRIDES[locale as keyof typeof LANGUAGE_OVERRIDES] ??
    LocaleCode.getLanguageName(reformattedLocale);
  const country =
    COUNTRY_OVERRIDES[locale as keyof typeof COUNTRY_OVERRIDES] ??
    LocaleCode.getCountryName(reformattedLocale);
  return `${language} (${country})`;
};

export const getLanguageNameFromLocaleCode = (locale: string) => {
  const [language] = locale.split('-');
  if (LANGUAGE_MAP[language as keyof typeof LANGUAGE_MAP]) {
    return `${LANGUAGE_MAP[language as keyof typeof LANGUAGE_MAP]}`;
  }
  return LocaleCode.getLanguageName(locale);
};

export const getCountryCodeFromLocaleCode = (locale: string) => {
  // get country code, use the second part of the locale, or return N/A if not found
  return LocaleCode.getCountryCode(locale) || locale.split('-')?.[1] || 'N/A';
};

export const getSluggifiedName = (name: string) => {
  switch (true) {
    case name.includes('-UK'):
      return 'united-kingdom';
    case name.includes('-HI'):
      return 'india';
    case name.includes('-KR'):
      return 'south-korea';
    case name.includes('-CN'):
      return 'china';
    default:
      return null;
  }
};

export const getMissingCountryName = (name: string) => {
  switch (true) {
    case name.includes('-UK'):
      return 'United Kingdom';
    case name.includes('-HI'):
      return 'India';
    case name.includes('-KR'):
      return 'South Korea';
    case name.includes('-CN'):
      return 'China';
    default:
      return null;
  }
};

const deriveCountryName = (locale: string) => {
  const normalizedLocale = locale.includes('_')
    ? locale.replace('_', '-')
    : locale;
  let countryName = LocaleCode.getCountryName(normalizedLocale);
  if (countryName?.includes('Korea')) {
    countryName = 'South Korea';
  } else if (countryName?.includes('Iran')) {
    countryName = 'Iran';
  } else if (countryName?.includes('Syrian')) {
    countryName = 'Syria';
  }

  const country = countryName ?? getMissingCountryName(normalizedLocale);
  return country;
};

export const getCountryNameSluggified = (actor: Actor) => {
  const { locale } = actor.variants[0];
  return getCountryNameSluggifiedFromLocale(locale);
};

export const getCountryNameSluggifiedFromLocale = (locale: string) => {
  const rawName = locale ? deriveCountryName(locale) : null;
  const slugifiedName = rawName
    ? rawName.toLowerCase().replace(/\s+/g, '-')
    : getSluggifiedName(locale);
  return slugifiedName;
};
/**
 * Given an actor's variants, return a composed string of languages and dialects
 * English (UK, IE)
 * English (MX), Spanish (MX)
 */
export const mapLocales = (actor: Actor) => {
  const languagesMap: Record<string, string[]> = {};
  actor.variants.forEach(v => {
    const language = getLanguageNameFromLocaleCode(v.locale);
    if (languagesMap[language] === undefined) {
      languagesMap[language] = [];
    }
    const country = deriveCountryName(v.locale);
    languagesMap[language].push(country);
  });
  const languages = Object.keys(languagesMap);
  const composedSubLanguageStrings = languages.map(l => {
    const dialects = uniq(languagesMap[l]);
    return `${l} (${dialects.toString().replace(',', ', ')})`;
  });
  if (composedSubLanguageStrings.length > 1) {
    return composedSubLanguageStrings.join(', ');
  }
  return composedSubLanguageStrings.toString();
};

export const mapLocale = (locale: string) => {
  const language = getLanguageNameFromLocaleCode(locale);
  const countryCode = getCountryCodeFromLocaleCode(locale);
  return `${language} (${countryCode})`;
};

/**
 * Format: "Conversational - Narration - Promo"
 */
export const labelFormatter = (labels: string[]) => {
  return uniq(labels)
    .sort((a, b) => a.localeCompare(b))
    .join(' - ');
};

type LooseObject = Record<string, any>;

export interface AvatarVariantAbilityContext {
  supportsCues: boolean;
  supportsRespellings: boolean;
  respellingFormat: 'v10' | 'v12' | null;
  supportsSmartToolbar: boolean;
  supportsCaptions: boolean;
  supportsReplacements: boolean;
  language: string;
  features: string[]; // from TTSModelRelease.features
  cues: {
    supported: boolean;
    loudness: boolean;
    tempo: boolean;
    respell: boolean;
    pause: boolean;
    pitch: boolean;
  } | null;
  toolbar: {
    supported: boolean;
    rewriteForSpeechEnabled: boolean;
    smartSuggestionsEnabled: boolean;
    oxfordSuggestionsEnabled: boolean;
    addReplacementEnabled: boolean;
    emphasisEnabled: boolean;
    replacementCharacterSupported: boolean;
  };
  meta: LooseObject;
  isRTL: boolean;
}

const RTL_LANGUAGE_CODES = ['ar', 'fa'];

/** True when `modelVersion` matches a released TTS model on the variant (latest / previous / next / preview). */
export function variantHasTtsModelVersion(
  variant: ActorVariant,
  modelVersion: string
): boolean {
  return (
    variant.versions.latest.model === modelVersion ||
    variant.versions.previous?.model === modelVersion ||
    variant.versions.next?.model === modelVersion ||
    variant.versions.preview?.model === modelVersion
  );
}

export function getAvatarVariantAbilityContext(
  avatarVariant: ActorVariant | undefined,
  selectedVersion: string | undefined
): AvatarVariantAbilityContext | null {
  if (!avatarVariant) return null;
  let features: string[] = [];
  let versionType: 'latest' | 'previous' | 'next' | 'preview' | null = null;
  if (avatarVariant.versions.latest.model === selectedVersion) {
    features = avatarVariant.versions.latest.features;
    versionType = 'latest';
  }
  if (avatarVariant.versions.previous?.model === selectedVersion) {
    features = avatarVariant.versions.previous?.features || [];
    versionType = 'previous';
  }
  if (avatarVariant.versions.next?.model === selectedVersion) {
    features = avatarVariant.versions.next?.features || [];
    versionType = 'next';
  }
  if (avatarVariant.versions.preview?.model === selectedVersion) {
    features = avatarVariant.versions.preview?.features || [];
    versionType = 'preview';
  }

  let supportsCaptions = false;
  let supportsRespellings = false;
  let hasV10 = false;
  const smartToolbarConfig = {
    supported: false,
    rewriteForSpeechEnabled: false,
    smartSuggestionsEnabled: false,
    oxfordSuggestionsEnabled: false,
    emphasisEnabled: false,
  };
  const isRTL = RTL_LANGUAGE_CODES.includes(
    LocaleCode.getLanguageCode(avatarVariant.locale)
  );
  const cues = {
    supported: false,
    loudness: false,
    tempo: false,
    respell: false,
    pause: false,
    pitch: false,
  };
  const replacements = {
    supported: false,
    payload: {
      type: 'none',
    },
  };
  for (const feature of features) {
    if (feature.includes('cues')) {
      cues.supported = true;
    }
    if (feature.includes('captions')) supportsCaptions = true;
    if (feature.includes('respell')) supportsRespellings = true;
    if (feature.includes('v10')) hasV10 = true;
    if (feature.includes('smart_toolbar:supported'))
      smartToolbarConfig.supported = true;
    if (feature.includes('smart_toolbar:rewrite_for_speech'))
      smartToolbarConfig.rewriteForSpeechEnabled = true;
    if (feature.includes('smart_toolbar:smart_suggestions'))
      smartToolbarConfig.smartSuggestionsEnabled = true;
    if (feature.includes('smart_toolbar:oxford_suggestions'))
      smartToolbarConfig.oxfordSuggestionsEnabled = true;
    if (feature.includes('smart_toolbar:emphasis'))
      smartToolbarConfig.emphasisEnabled = true;
    if (feature.includes('replacements')) {
      replacements.supported = true;
      replacements.payload.type = feature;
    }
    if (feature.includes('cues:loudness')) cues.loudness = true;
    if (feature.includes('cues:tempo')) cues.tempo = true;
    if (feature.includes('cues:respell')) cues.respell = true;
    if (feature.includes('cues:pause')) cues.pause = true;
    if (feature.includes('cues:pitch')) cues.pitch = true;
  }
  return {
    supportsCues: cues.supported,
    supportsCaptions,
    supportsRespellings,
    // eslint-disable-next-line no-nested-ternary
    respellingFormat: supportsRespellings ? (hasV10 ? 'v10' : 'v12') : null,
    supportsSmartToolbar: smartToolbarConfig.supported,
    supportsReplacements: replacements.supported,
    isRTL,
    cues,
    language: avatarVariant.locale,
    /**
     * @see https://docs.google.com/spreadsheets/d/1qeRYU5RN8bEHnj8f8KzIDVbYg-UzBuUj9XZHb9UFUzM/edit?gid=0#gid=0
     */
    toolbar: {
      supported: smartToolbarConfig.supported,
      rewriteForSpeechEnabled: smartToolbarConfig.rewriteForSpeechEnabled,
      smartSuggestionsEnabled: smartToolbarConfig.smartSuggestionsEnabled,
      oxfordSuggestionsEnabled: smartToolbarConfig.oxfordSuggestionsEnabled,
      addReplacementEnabled: smartToolbarConfig.supported,
      emphasisEnabled: smartToolbarConfig.emphasisEnabled,
      // not in use but conforming to matrix in case we need use later...
      replacementCharacterSupported: smartToolbarConfig.supported,
    },
    features,
    meta: {
      replacements,
      versionType,
    },
  };
}

export const PREVIEW_MODEL_TTS_VERSION = 'v13.3';
export const PREVIEW_MODEL_DISPLAY_NAME = 'Preview 13.3';
export const PREVIEW_MODEL_FILTER_VALUE = 'preview';
export const PREVIEW_MODEL_FILTER_LABEL = 'Preview (13.3)';
export const PREVIEW_MODEL_FILTER_DESCRIPTION =
  'Our experimental model, still in development. Use it to test and evaluate, not for production workflows.';

export function getTtsModelDisplayName(
  ttsVersion: string,
  avatar?: Actor,
  variant?: ActorVariant
): string {
  if (avatar && isPartnerVoice(avatar)) {
    return 'Partner';
  }
  if (variant?.versions.preview?.model === ttsVersion) {
    return PREVIEW_MODEL_DISPLAY_NAME;
  }
  if (ttsVersion.startsWith('v12')) {
    return 'Caruso';
  }
  if (ttsVersion.startsWith('v11')) {
    return 'Custom';
  }
  if (ttsVersion.startsWith('v10')) {
    return 'Standard';
  }
  return 'Default';
}

export type AvatarDropdownItem = {
  variant: ActorVariant;
  modelName: string;
  locale: string;
};

export type AvatarDropdownSubMenu = {
  header: string;
  items: AvatarDropdownItem[];
};

export type AvatarDropdownSection = {
  header: string;
  tooltip?: string;
  subheader?: string;
  items: AvatarDropdownItem[];
  subMenu?: AvatarDropdownSubMenu[];
};

export function flattenDropdownItems(
  sections: AvatarDropdownSection[]
): AvatarDropdownItem[] {
  return sections.flatMap(section => [
    ...section.items,
    ...(section.subMenu?.flatMap(sub => sub.items) ?? []),
  ]);
}

export type DeriveDropdownItemsOptions = {
  includePreviewModels?: boolean;
};

export function isPreviewTtsModel(
  variant: ActorVariant | undefined,
  ttsVersion: string | undefined
): boolean {
  return Boolean(ttsVersion && variant?.versions.preview?.model === ttsVersion);
}

export function deriveDropdownItems(
  actor: Actor,
  options: DeriveDropdownItemsOptions = {}
): AvatarDropdownSection[] {
  const { includePreviewModels = false } = options;
  const CARUSO_MODEL = 'v12';
  const STANDARD_MODEL = 'v10';
  const PREVIEW_MODEL = 'preview';

  const sectionConfigs = {
    [CARUSO_MODEL]: {
      header: 'Caruso',
      subheader: 'Cues available',
      tooltip: `Our most advanced model with expanded features and greater flexibility for fine-tuned control.`,
    },
    [STANDARD_MODEL]: {
      header: 'Standard',
      subheader: 'Cues not supported',
      tooltip: `Our legacy model offering essential tools for quick, reliable adjustments.`,
    },
    [PREVIEW_MODEL]: {
      header: 'Preview models',
      subheader: 'Test our latest models before a full release.',
      tooltip:
        'Previews are under active development and not recommended for production workflows. You can use them to generate audio and evaluate quality, but models may change or be removed as we refine them.',
    },
  };

  const sectionOrder = ['Caruso', 'Standard', 'Preview models'];

  const sectionsMap = new Map<string, AvatarDropdownSection>();
  const previewSubMenuMap = new Map<string, AvatarDropdownItem[]>();

  for (const variant of actor.variants) {
    const models = [
      variant.versions.previous?.model,
      variant.versions.latest.model,
      variant.versions.next?.model,
      ...(includePreviewModels ? [variant.versions.preview?.model] : []),
    ].filter((model): model is string => !!model);

    // if voice is partner model, return a single section with the partner model
    // no additional work is required
    if (actor && isPartnerVoice(actor)) {
      return [
        {
          header: 'Partner',
          items: [
            {
              variant,
              modelName: variant.versions.latest.model,
              locale: `(${getCountryCodeFromLocaleCode(variant.locale)})`,
            },
          ],
        },
      ];
    }

    for (const model of models) {
      if (includePreviewModels && model === variant.versions.preview?.model) {
        if (!previewSubMenuMap.has(PREVIEW_MODEL_DISPLAY_NAME)) {
          previewSubMenuMap.set(PREVIEW_MODEL_DISPLAY_NAME, []);
        }
        previewSubMenuMap.get(PREVIEW_MODEL_DISPLAY_NAME)!.push({
          variant,
          modelName: model,
          locale: `(${getCountryCodeFromLocaleCode(variant.locale)})`,
        });
      } else {
        let modelType: string | null = null;
        if (model.includes(CARUSO_MODEL)) {
          modelType = CARUSO_MODEL;
        } else if (model.includes(STANDARD_MODEL)) {
          modelType = STANDARD_MODEL;
        }

        if (modelType) {
          if (!sectionsMap.has(modelType)) {
            sectionsMap.set(modelType, {
              ...sectionConfigs[modelType as keyof typeof sectionConfigs],
              items: [],
            });
          }
          sectionsMap.get(modelType)!.items.push({
            variant,
            modelName: model,
            locale: `(${getCountryCodeFromLocaleCode(variant.locale)})`,
          });
        }
      }
    }
  }

  if (previewSubMenuMap.size > 0 && includePreviewModels) {
    sectionsMap.set(PREVIEW_MODEL, {
      ...sectionConfigs[PREVIEW_MODEL],
      items: [],
      subMenu: Array.from(previewSubMenuMap.entries()).map(
        ([header, items]) => ({
          header,
          items,
        })
      ),
    });
  }

  const sections = Array.from(sectionsMap.values()).filter(
    section =>
      section.items.length > 0 ||
      section.subMenu?.some(sub => sub.items.length > 0)
  );

  return sections.sort((a, b) => {
    const aIndex = sectionOrder.indexOf(a.header);
    const bIndex = sectionOrder.indexOf(b.header);
    return (
      (aIndex === -1 ? sectionOrder.length : aIndex) -
      (bIndex === -1 ? sectionOrder.length : bIndex)
    );
  });
}

export const getIconForVariantType = (
  variantType: ActorVariantType
): AvailableIconTypes => {
  switch (variantType.label.toLowerCase()) {
    case 'narration':
      return 'bookOpen02';
    case 'promo':
      return 'announcement01';
    case 'conversational':
      return 'messageTextSquare02';
    case 'character':
      return 'faceSmile';
    default:
      return 'bookOpen02';
  }
};

export function flattenAvatarsAndVariants(avatars: Actor[]) {
  const lookup: {
    avatarById: Record<number, Actor>;
    variant: Record<number, ActorVariant>;
    variantByAvatarId: Record<number, Record<number, ActorVariant>>;
    avatarByVariantId: Record<number, Actor>;
    modelReleaseVersionsByVariantId: Record<
      number,
      {
        previous: ActorVariantVersions['previous'];
        latest: ActorVariantVersions['latest'];
        next: ActorVariantVersions['next'];
        preview: ActorVariantVersions['preview'];
        list: (
          | ActorVariantVersions['previous']
          | ActorVariantVersions['latest']
          | ActorVariantVersions['next']
          | ActorVariantVersions['preview']
        )[];
      }
    >;
  } = {
    avatarById: {},
    variant: {},
    variantByAvatarId: {},
    avatarByVariantId: {},
    modelReleaseVersionsByVariantId: {},
  };
  for (const avatar of avatars) {
    lookup.avatarById[avatar.id] = avatar;
    for (const variant of avatar.variants) {
      lookup.avatarByVariantId[variant.id] = avatar;
      lookup.variant[variant.id] = variant;
      lookup.modelReleaseVersionsByVariantId[variant.id] = {
        previous: variant.versions.previous,
        latest: variant.versions.latest,
        next: variant.versions.next,
        preview: variant.versions.preview,
        list: [
          variant.versions.previous,
          variant.versions.latest,
          variant.versions.next,
          variant.versions.preview,
        ],
      };
      if (!lookup.variantByAvatarId[avatar.id]) {
        lookup.variantByAvatarId[avatar.id] = {};
      }
      lookup.variantByAvatarId[avatar.id][variant.id] = variant;
    }
  }
  return lookup;
}

export const getRegionalAvatars = (locale: LocaleEnum_V2, avatars: Actor[]) => {
  const normalizedLocale = locale.replace('_', '-');

  return avatars
    .map(avatar => ({
      ...avatar,
      variants: avatar.variants.filter(
        variant => variant.locale === normalizedLocale
      ),
    }))
    .filter(avatar => avatar.variants.length > 0);
};
